import { Component, ViewChild, input, output } from '@angular/core';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { userDtos, userEnums, webEnums } from '@vectronic/openApi-customer';
import { roleRank } from '@vectronic/util';

@Component({
  selector: 'table-role-selector',
  templateUrl: './role-selector.component.html',
  styleUrls: ['./role-selector.component.scss'],
  imports: [MatSelectModule],
})
export class RoleSelectorComponent {
  @ViewChild(MatSelect) private roleSelect?: MatSelect;

  row = input<userDtos.Share>();
  ownRole = input<webEnums.Role>();
  currentUserEmail = input<string>();
  pendingRequestedShares = input<userDtos.SelfRequestedShare[]>([]);
  allowOwner = input.required<boolean>();
  hideDeviceManager = input.required<boolean>();
  numberOfOwners = input.required<number>();

  updateShare = output<userDtos.Share>();
  requestHigherRole = output<Exclude<userEnums.Role, 'OWNER'>>();
  withdrawRequestedShares = output<userDtos.SelfRequestedShare[]>();
  revokeAccess = output<void>();

  rowIsOwner() {
    return this.row().role === 'OWNER';
  }

  transformedRole() {
    const { role } = this.row();
    if (role === 'DATA_MANAGER') return 'Data Manager';
    else if (role === 'DEVICE_MANAGER') return 'Device Manager';
    else if (role === 'OWNER') return 'Owner';
    else if (role === 'READER') return 'Reader';
  }

  isOwnRow() {
    return this.row().email === this.currentUserEmail();
  }

  canUpgradeToRole(role: string): boolean {
    if (role === 'OWNER') return false;
    if (!this.isOwnRow()) return false;
    return roleRank(role) > roleRank(this.row().role);
  }

  canManageRow(): boolean {
    const ownRole = this.ownRole();
    const rowRole = this.row()?.role;
    if (!ownRole || !rowRole) return false;

    if (this.isOwnRow()) return true;
    if (ownRole === 'OWNER') return true;
    return roleRank(rowRole) <= roleRank(ownRole);
  }

  canSelectRole(role: userEnums.Role): boolean {
    if (this.canUpgradeToRole(role)) return false;
    if (this.isOwnRow()) return true;
    if (!this.canManageRow()) return false;
    if (role === 'OWNER') return this.ownRole() === 'OWNER' && this.allowOwner();
    return roleRank(role) <= roleRank(this.ownRole());
  }

  canShowUpgradeAction(role: Exclude<userEnums.Role, 'OWNER'>): boolean {
    return (
      this.canUpgradeToRole(role) &&
      (!this.hasPendingRequestForAnyRole() || this.hasPendingRequest(role))
    );
  }

  hasPendingRequest(role: Exclude<userEnums.Role, 'OWNER'>): boolean {
    return Boolean(this.pendingRequestsForRole(role).length);
  }

  hasPendingRequestForAnyRole(): boolean {
    return Boolean(this.pendingRequestedShares().length);
  }

  pendingRequestsForRole(role: Exclude<userEnums.Role, 'OWNER'>) {
    return this.pendingRequestedShares().filter(({ requestedRole }) => requestedRole === role);
  }

  onUpdateShare(role: userEnums.Role | 'REVOKE') {
    if (role === 'REVOKE') {
      this.roleSelect?.close();
      this.revokeAccess.emit();
      setTimeout(() => this.roleSelect?.writeValue(this.row().role));
      return;
    }
    if (this.canUpgradeToRole(role)) return; // icon-only path; ignore keyboard selection
    if (!this.canSelectRole(role)) return;
    this.updateShare.emit({ role, email: this.row().email });
  }

  onRequestHigherRole(role: Exclude<userEnums.Role, 'OWNER'>) {
    this.roleSelect?.close();
    this.requestHigherRole.emit(role);
  }

  onWithdrawRequestedShare(role: Exclude<userEnums.Role, 'OWNER'>) {
    const requests = this.pendingRequestsForRole(role);
    if (!requests.length) return;
    this.roleSelect?.close();
    this.withdrawRequestedShares.emit(requests);
  }
}
